/* SUMMARY
  * partExportDescriptor: mapping data/xlsx
*/

import { t } from "i18next";
import { getPartTypeI18nKey } from "../../utils/partTypeUtils";
import { isOdooConnected } from "../../odoo/odoo";

/**
* partExportDescriptor: mapping data/xlsx
*
* Covers what the parts table shows plus the commercial fields a stock export is asked for. Every
* getter is defensive: `category` and `barcode` are optional on EasyTech.Part, and the prices are
* absent on parts that arrived from Odoo without them — `String(undefined)` would have written the
* literal "undefined" into the cell.
*/
export const partExportDescriptor: EasyTech.ExportDescriptor[] = [
  {
    column: "id",
    columnTranslationKey: "common:export.columns.id",
    // Identifiant technique, demandé pour rapprocher un export d'un autre système. Décoché comme
    // n'importe quelle autre colonne si le lecteur n'en a pas l'usage.
    getter: (part: EasyTech.Part) => part.id || "",
  },
  {
    column: "label",
    columnTranslationKey: "common:label",
    getter: (part: EasyTech.Part) => part.label,
  },
  {
    column: "reference",
    columnTranslationKey: "common:ref.label",
    getter: (part: EasyTech.Part) => part.ref,
  },
  {
    column: "category",
    columnTranslationKey: "common:category",
    getter: (part: EasyTech.Part) => part.category?.label || "",
  },
  {
    column: "type",
    columnTranslationKey: "common:type",
    // Le même libellé traduit que la table affiche, pas le jeton brut : getPartTypeI18nKey rend
    // undefined pour une valeur inconnue, auquel cas la table retombe sur le jeton — on fait pareil.
    getter: (part: EasyTech.Part) => {
      if (!part.type) return "";
      const key = getPartTypeI18nKey(part.type);
      return key ? t(key) : part.type;
    },
  },
  {
    column: "unity",
    columnTranslationKey: "common:unity",
    getter: (part: EasyTech.Part) => part.unity || "",
  },
  {
    column: "price",
    columnTranslationKey: "common:sellingPrice",
    getter: (part: EasyTech.Part) => (part.price != null ? String(part.price) : ""),
  },
  {
    column: "purchase price",
    columnTranslationKey: "common:export.columns.purchasePrice",
    getter: (part: EasyTech.Part) => (part.purchasePrice != null ? String(part.purchasePrice) : ""),
  },
  {
    column: "barcode",
    columnTranslationKey: "common:export.columns.barcode",
    getter: (part: EasyTech.Part) => part.barcode || "",
  }
];
/***/

/**
* odooExternalIdColumn: l'identifiant Odoo de l'article.
*
* Même traitement que côté clients : hors realm Odoo la colonne serait vide sur toutes les lignes,
* et une colonne toujours vide dans la liste des champs se lit comme une donnée manquante plutôt que
* comme une donnée sans objet.
*/
const odooExternalIdColumn: EasyTech.ExportDescriptor = {
  column: "odoo external id",
  columnTranslationKey: "common:export.columns.odooExternalId",
  // Écrit en texte : un identifiant se lit, ne se calcule pas, et le laisser en nombre ferait manger
  // un zéro de tête par le tableur.
  getter: (part: EasyTech.Part) =>
    part.externalId === undefined || part.externalId === null ? "" : String(part.externalId)
};

/**
* getPartExportDescriptor: le descripteur pièces adapté au realm courant.
*
* Une fonction, comme pour les clients : la disponibilité d'Odoo dépend du realm lu dans le stockage
* local, qu'une constante évaluée à l'import figerait au chargement de la page.
*/
export const getPartExportDescriptor = (): EasyTech.ExportDescriptor[] =>
  isOdooConnected()
    ? [...partExportDescriptor, odooExternalIdColumn]
    : partExportDescriptor;
/***/
